// Global Instructions Rule Applied!

/**
 * InputManager Component
 *
 * Main container component for the InputManager system.
 * Manages a collection of fields with inline editing capabilities for both forms and table cells.
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { Row, Col } from 'antd'
import { InputManagerProps } from './types.ts'
import { InputManagerField } from './InputManagerField.tsx'
import { FieldLabelContent } from './components/FieldLabelContent.tsx'
import { useInputManager } from './hooks/useInputManager.ts'
import { usePatchRequest, extractFullFieldName } from './hooks/usePatchRequest.ts'
import { toast } from 'react-toastify'
import { MESSAGE_UPDATE_SUCCESSFUL } from '../../../state_manager/constants/strings.ts'
import { syncUpdatesToState } from '../../../state_manager/sync_engine/controller.ts'
import { isRequiredFieldValid } from './utils/formatters.ts'
import { FieldConfig } from './types.ts'

// ============================================================================
// Component Implementation
// ============================================================================

/**
 * InputManager Component
 * Provides unified inline editing functionality for both forms and table cells
 */
export const InputManager: React.FC<InputManagerProps> = ({
  inputMode,
  fields,
  labelPosition = 'inline',
  alignment = 'left',
  onChange,
  onSave,
  enableAutoPatch = false,
  patchId, // Used by patch request
  recordId, // Used for onPatchSuccess callback to update the record in the redux store
  onError,
  onPatchSuccess,
  validationErrors,
  validationErrorsVersion,
  className,
  style,
  onClick,
  onFieldEdit,
  showLabelColons = true,
  fieldWrapping = false,
  textClassName,
  maxLabelWidth: maxLabelWidthProp,
  currentRecord: externalCurrentRecord
}) => {
  // ============================================================================
  // State Management
  // ============================================================================

  const { editingFieldId, startEdit, stopEdit, isFieldEditing } = useInputManager()
  const { patchField, isPatching } = usePatchRequest()

  // Local state for field values and editing
  const [fieldValues, setFieldValues] = useState<Record<string, any>>(() => {
    const initialValues: Record<string, any> = {}
    fields.forEach((field) => {
      initialValues[field.key] = field.value
    })
    return initialValues
  })

  const [editingFieldData, setEditingFieldData] = useState<Record<string, any>>({})
  const [fieldValidationErrors, setFieldValidationErrors] = useState<Record<string, string>>(validationErrors ?? {})
  // Track which field should be focused when validation fails
  const [firstErrorFieldKey, setFirstErrorFieldKey] = useState<string | null>(null)

  // Use ref to track previous field values for comparison
  const prevFieldValuesRef = useRef<Record<string, any>>({})
  const prevFieldsRef = useRef<typeof fields>([])
  // Track fields that have been locally modified to prevent overwriting during typing
  const locallyModifiedFieldsRef = useRef<Set<string>>(new Set())
  // Track which error field we've already focused to prevent re-focusing during typing
  const focusedErrorFieldRef = useRef<string | null>(null)
  // Track timeout for clearing focus when user types (similar to DealCommitments)
  const focusClearTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Update field values when props change
  useEffect(() => {
    // Extract current field values from field configs
    const currentFieldValues: Record<string, any> = {}
    for (const f of fields) {
      currentFieldValues[f.key] = f.value
    }

    // Compare with previous values using ref (not state) to avoid dependency loop
    const prevValues = prevFieldValuesRef.current
    const locallyModified = locallyModifiedFieldsRef.current

    // In edit mode, skip updating fields that are being actively edited
    // This prevents cursor position loss during typing
    const fieldsToUpdate: Record<string, any> = {}
    for (const key in currentFieldValues) {
      // Only update if not locally modified (prevents overwriting during typing)
      if (!locallyModified.has(key)) {
        const current = currentFieldValues[key]
        const previous = prevValues[key]
        // Check if value actually changed
        if (current !== previous) {
          // Use JSON.stringify for deep comparison if values are different by reference
          if (typeof current === 'object' || typeof previous === 'object') {
            if (JSON.stringify(current) !== JSON.stringify(previous)) {
              fieldsToUpdate[key] = current
            }
          } else {
            fieldsToUpdate[key] = current
          }
        }
      }
    }

    // Check if any fields changed (including new fields)
    const hasChanged =
      Object.keys(currentFieldValues).length !== Object.keys(prevValues).length ||
      Object.keys(fieldsToUpdate).length > 0

    // Only update if values actually changed
    if (hasChanged) {
      setFieldValues((prev) => {
        // Merge updates, preserving locally modified fields
        const updated = { ...prev }
        for (const key in fieldsToUpdate) {
          if (!locallyModified.has(key)) {
            updated[key] = fieldsToUpdate[key]
          }
        }
        // Add any new fields that don't exist in prev
        for (const key in currentFieldValues) {
          if (!(key in updated) && !locallyModified.has(key)) {
            updated[key] = currentFieldValues[key]
          }
        }
        return updated
      })
      // Update prevFieldValuesRef, preserving locally modified fields
      const updatedPrev = { ...prevValues }
      for (const key in fieldsToUpdate) {
        if (!locallyModified.has(key)) {
          updatedPrev[key] = fieldsToUpdate[key]
        }
      }
      for (const key in currentFieldValues) {
        if (!(key in updatedPrev) && !locallyModified.has(key)) {
          updatedPrev[key] = currentFieldValues[key]
        }
      }
      prevFieldValuesRef.current = updatedPrev
    }

    // Track fields for reference equality check
    prevFieldsRef.current = fields
  }, [fields])

  // Update validation errors when props change
  useEffect(() => {
    if (validationErrors) {
      setFieldValidationErrors(validationErrors)

      // If validation errors are provided externally, find the first required field with an error
      // Only set focus if no field is currently being edited (to avoid interrupting typing)
      if (!editingFieldId && Object.keys(validationErrors).length > 0) {
        const firstErrorField = fields.find((f) => {
          return f.required && validationErrors[f.key] && !isRequiredFieldValid(fieldValues[f.key])
        })
        if (firstErrorField) {
          setFirstErrorFieldKey(firstErrorField.key)
        }
      }
    } else {
      // Clear focus when validation errors are cleared
      setFirstErrorFieldKey(null)
    }
  }, [validationErrors, editingFieldId, fields, fieldValues])

  // Explicit submit (Save click): the parent bumps validationErrorsVersion.
  // Unlike the prop-sync effect above, this must fire even when the first
  // error is the SAME field as last time and even when an input still holds
  // focus (Safari/Firefox leave focus in the last input when a button is
  // clicked), otherwise a second Save click looks dead. Blur the input so the
  // focus effect's typing guard stays untouched for auto-patch surfaces.
  const prevErrorsVersionRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (validationErrorsVersion === undefined || validationErrorsVersion === prevErrorsVersionRef.current) {
      return
    }
    prevErrorsVersionRef.current = validationErrorsVersion
    if (!validationErrors || Object.keys(validationErrors).length === 0) return
    // On explicit submit the parent's errors are authoritative — no required /
    // value re-check here.
    const firstErrorField = fields.find((f) => validationErrors[f.key])
    if (!firstErrorField) return
    const active = document.activeElement as HTMLElement | null
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
      active.blur()
    }
    focusedErrorFieldRef.current = null
    setFirstErrorFieldKey(firstErrorField.key)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationErrorsVersion])

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Handle starting to edit a field
   */
  const handleStartEdit = useCallback(
    (fieldKey: string) => {
      const field = fields.find((f) => f.key === fieldKey)
      if (!field || field.editable === false) return

      // Check if another field is being edited - prevent switching
      if (editingFieldId && editingFieldId !== fieldKey) {
        // Another field is being edited, don't allow switching
        return
      }

      startEdit(fieldKey)
      setEditingFieldData((prev) => ({
        ...prev,
        [fieldKey]: fieldValues[fieldKey]
      }))

      // In edit mode, mark field as locally modified to prevent prop updates during typing
      if (inputMode === 'edit') {
        locallyModifiedFieldsRef.current.add(fieldKey)
      }
    },
    [fields, editingFieldId, startEdit, fieldValues, inputMode]
  )

  /**
   * Handle confirming/saving field changes
   */
  const handleConfirm = useCallback(
    async (fieldKey: string, overrideValue?: any) => {
      const field = fields.find((f) => f.key === fieldKey)
      if (!field) return

      const newValue = overrideValue !== undefined ? overrideValue : editingFieldData[fieldKey]
      const originalValue = fieldValues[fieldKey]

      // Only proceed if value actually changed
      // For arrays, do deep comparison
      let valuesEqual = false
      if (Array.isArray(newValue) && Array.isArray(originalValue)) {
        // Compare array lengths and values
        if (newValue.length !== originalValue.length) {
          valuesEqual = false
        } else {
          // Compare each element - for objects, compare by value/id
          valuesEqual = newValue.every((newItem, index) => {
            const origItem = originalValue[index]
            if (typeof newItem === 'object' && typeof origItem === 'object') {
              const newId = newItem.value || newItem.id || newItem
              const origId = origItem.value || origItem.id || origItem
              return newId === origId
            }
            return newItem === origItem
          })
        }
      } else {
        valuesEqual = newValue === originalValue
      }

      if (valuesEqual) {
        stopEdit(fieldKey)
        // Clear locally modified flag
        locallyModifiedFieldsRef.current.delete(fieldKey)
        return
      }

      // Validate required fields before proceeding
      if (field.required && !isRequiredFieldValid(newValue)) {
        // Set validation error
        const errorMessage = field.label ? `${field.label} is required` : 'This field is required'
        setFieldValidationErrors((prev) => ({
          ...prev,
          [fieldKey]: errorMessage
        }))

        // Find the first required field with an error to focus
        // Check all fields to find the first required field with a validation error
        const firstErrorField = fields.find((f) => {
          // Check if this field is required and has an error
          if (!f.required) return false
          // Check if this field has a validation error (either existing or the one we just set)
          const hasError = fieldKey === f.key || fieldValidationErrors[f.key]
          if (!hasError) return false
          // Check if the field value is invalid
          const fieldValue = f.key === fieldKey ? newValue : fieldValues[f.key]
          return !isRequiredFieldValid(fieldValue)
        })

        // Focus the first required field with an error (or the current field if it's the first)
        if (firstErrorField) {
          setFirstErrorFieldKey(firstErrorField.key)
        } else {
          // Fallback to current field if no other errors found
          setFirstErrorFieldKey(fieldKey)
        }

        // Don't proceed with save/patch - keep field in edit mode
        return
      }

      // Optional per-field confirmation gate (e.g. warn before consequential changes)
      if (field.confirmPatch) {
        const confirmRecord =
          externalCurrentRecord ||
          fields.reduce(
            (acc, f) => {
              acc[f.key] = f.value
              return acc
            },
            {} as Record<string, any>
          )
        const proceed = await field.confirmPatch(newValue, confirmRecord)
        if (!proceed) {
          // Abort: discard the pending edit and leave the original value untouched
          setEditingFieldData((prev) => {
            const newData = { ...prev }
            delete newData[fieldKey]
            return newData
          })
          setFieldValidationErrors((prev) => {
            const newErrors = { ...prev }
            delete newErrors[fieldKey]
            return newErrors
          })
          // In edit mode handleValueChange has ALREADY written the new value
          // into fieldValues (it does that up front so typing doesn't lose the
          // caret), and that is what the field renders from. Clearing the
          // pending edit is therefore not enough — the declined value has to
          // be put back, or a cancelled confirmation leaves the control
          // showing a change that was never saved. (Found 2026-09-21: cancelling
          // the funding-round dialog left "Is Funding Round" ticked over a deal
          // the backend never touched.)
          setFieldValues((prev) => ({ ...prev, [fieldKey]: field.value }))
          stopEdit(fieldKey)
          locallyModifiedFieldsRef.current.delete(fieldKey)
          return
        }
      }

      try {
        setFieldValues((prev) => ({ ...prev, [fieldKey]: newValue }))

        // Use pre-change field values so valueTransformer can compute diff (e.g. detect removed contacts for delete)
        const currentRecord =
          externalCurrentRecord ||
          fields.reduce(
            (acc, f) => {
              acc[f.key] = f.value
              return acc
            },
            {} as Record<string, any>
          )

        let transformedFields: Record<string, any> | null = null
        if (field.valueTransformer && currentRecord) {
          transformedFields = field.valueTransformer(newValue, currentRecord)
          const fieldKeysSet = new Set(fields.map((f) => f.key))
          Object.keys(transformedFields).forEach((key) => {
            if (key !== fieldKey && fieldKeysSet.has(key)) {
              setFieldValues((prev) => ({ ...prev, [key]: transformedFields![key] }))
              onChange?.(key, transformedFields![key])
            }
          })
        }

        const onChangeResult = onChange?.(fieldKey, newValue) as any
        if (onChangeResult && typeof onChangeResult === 'object' && 'then' in onChangeResult) {
          await onChangeResult
        }

        if (enableAutoPatch && patchId && field.patchEndpoint !== '') {
          const patchResponse = await patchField(
            fieldKey,
            newValue,
            patchId,
            field.patchFields,
            field.patchEndpoint,
            field.valueTransformer,
            currentRecord
          )

          let updateData: Record<string, any> = {}

          if (transformedFields) {
            Object.keys(transformedFields).forEach((key) => {
              updateData[key] = transformedFields![key]
            })
          } else if (field.patchFields && field.patchFields.length > 0) {
            // Multi-field patches without valueTransformer
            field.patchFields.forEach(({ sourceKey, targetField }) => {
              if (newValue && typeof newValue === 'object' && sourceKey in newValue) {
                const reduxFieldName = field.reduxFieldMapping?.[targetField] || targetField
                updateData[reduxFieldName] = newValue[sourceKey]
              }
            })
          } else {
            // Single-field patch: extract full field name for Redux update
            const fullFieldName = extractFullFieldName(fieldKey)
            if (fullFieldName) {
              updateData[fullFieldName] = newValue
            }
          }

          // Sync updates to state (non-blocking, asynchronous)
          // Use patchResponse if available, otherwise use updateData
          const syncPayload =
            patchResponse && typeof patchResponse === 'object' && Object.keys(patchResponse).length > 0
              ? patchResponse
              : updateData

          if (Object.keys(syncPayload).length > 0) {
            syncUpdatesToState(syncPayload)
          }

          // Call onPatchSuccess callback after successful PATCH
          if (onPatchSuccess && Object.keys(updateData).length > 0) {
            // The PATCH only ran with a patchId, so the id that was actually patched is always known.
            onPatchSuccess(recordId ?? patchId, updateData)
          }

          onFieldEdit?.(false) // Notify parent that editing ended
          // Deduped: with save-on-click-away, tabbing through several fields
          // would otherwise stack one identical toast per field.
          toast.success(MESSAGE_UPDATE_SUCCESSFUL, { toastId: 'input-manager-autosave' })
        }

        // Call onSave callback (only called if auto-patch is disabled)
        if (!enableAutoPatch) {
          onSave?.(fieldKey, newValue)
        }

        // Clear any validation errors for this field
        setFieldValidationErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[fieldKey]
          return newErrors
        })

        // Clear focus state on successful save
        if (focusClearTimeoutRef.current) {
          clearTimeout(focusClearTimeoutRef.current)
          focusClearTimeoutRef.current = null
        }
        setFirstErrorFieldKey(null)
        focusedErrorFieldRef.current = null

        // Stop editing
        stopEdit(fieldKey)
        setEditingFieldData((prev) => {
          const newData = { ...prev }
          delete newData[fieldKey]
          return newData
        })

        // Clear locally modified flag so external updates can sync
        locallyModifiedFieldsRef.current.delete(fieldKey)
      } catch (error) {
        // Revert local state on error
        setFieldValues((prev) => ({
          ...prev,
          [fieldKey]: originalValue
        }))

        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
        setFieldValidationErrors((prev) => ({
          ...prev,
          [fieldKey]: errorMessage
        }))

        onError?.(fieldKey, error instanceof Error ? error : new Error(errorMessage))
      }
    },
    [
      fields,
      editingFieldData,
      fieldValues,
      fieldValidationErrors,
      enableAutoPatch,
      recordId,
      patchField,
      patchId,
      onChange,
      onSave,
      onError,
      onPatchSuccess,
      onFieldEdit,
      stopEdit,
      externalCurrentRecord
    ]
  )

  /**
   * Handle canceling field changes
   */
  const handleCancel = useCallback(
    (fieldKey: string) => {
      // Revert to original value
      setEditingFieldData((prev) => {
        const newData = { ...prev }
        delete newData[fieldKey]
        return newData
      })

      // Clear validation errors
      setFieldValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[fieldKey]
        return newErrors
      })

      // Clear focus state on cancel
      if (focusClearTimeoutRef.current) {
        clearTimeout(focusClearTimeoutRef.current)
        focusClearTimeoutRef.current = null
      }
      setFirstErrorFieldKey(null)
      focusedErrorFieldRef.current = null

      stopEdit(fieldKey)

      // Clear locally modified flag so external updates can sync
      locallyModifiedFieldsRef.current.delete(fieldKey)
    },
    [stopEdit]
  )

  /**
   * Handle field value changes during editing
   */
  const handleValueChange = useCallback(
    (fieldKey: string, newValue: any) => {
      // Don't clear firstErrorFieldKey when user types - only check if user is typing
      // This prevents focus from shifting. Focus will only change on new validation errors.
      // Mark that user is typing in this field to prevent focus effect from running
      if (firstErrorFieldKey === fieldKey) {
        // Mark as focused so focus effect doesn't re-run
        focusedErrorFieldRef.current = firstErrorFieldKey
      }

      setEditingFieldData((prev) => ({
        ...prev,
        [fieldKey]: newValue
      }))

      // For edit mode, update fieldValues immediately and mark as locally modified
      // This prevents the useEffect from overwriting the value and causing cursor loss
      if (inputMode === 'edit') {
        locallyModifiedFieldsRef.current.add(fieldKey)

        // Run valueTransformer for multi-field updates (e.g. Country clear -> City/State clear)
        let relatedFieldUpdates: Array<{ key: string; value: any }> = []
        let allTransformerUpdates: Array<{ key: string; value: any }> = []
        const field = fields.find((f) => f.key === fieldKey)
        if (field?.valueTransformer) {
          const currentRecord =
            externalCurrentRecord ||
            fields.reduce(
              (acc, f) => {
                acc[f.key] = fieldValues[f.key]
                return acc
              },
              {} as Record<string, any>
            )
          const transformedFields = field.valueTransformer(newValue, currentRecord)
          const fieldKeysSet = new Set(fields.map((f) => f.key))
          Object.keys(transformedFields).forEach((key) => {
            if (key !== fieldKey) {
              allTransformerUpdates.push({ key, value: transformedFields[key] })
              // Only update fieldValues for keys that exist as form fields
              if (fieldKeysSet.has(key)) {
                relatedFieldUpdates.push({ key, value: transformedFields[key] })
              }
            }
          })
        }

        setFieldValues((prev) => {
          const updates = { ...prev, [fieldKey]: newValue }
          relatedFieldUpdates.forEach(({ key, value }) => {
            updates[key] = value
            locallyModifiedFieldsRef.current.delete(key)
          })
          return updates
        })

        // Notify parent of ALL valueTransformer updates (including *_country_code etc.)
        // so formData stays in sync - City field's country prop depends on *_country_code
        allTransformerUpdates.forEach(({ key, value }) => onChange?.(key, value))
        onChange?.(fieldKey, newValue)
      }
    },
    [inputMode, onChange, firstErrorFieldKey, fields, fieldValues, externalCurrentRecord]
  )

  /**
   * Handle field blur (clicking outside or on another field)
   */
  const handleBlur = useCallback(
    (fieldKey: string) => {
      const hasChanges = editingFieldData[fieldKey] !== fieldValues[fieldKey]
      if (hasChanges) {
        // Show popconfirm will be handled by the field component
        return
      }
      // No changes, just cancel
      handleCancel(fieldKey)
    },
    [editingFieldData, fieldValues, handleCancel]
  )

  // ============================================================================
  // Render Helpers
  // ============================================================================

  /**
   * Determine if a field should be in edit mode
   */
  const shouldFieldBeEditing = useCallback(
    (fieldKey: string): boolean => {
      const field = fields.find((f) => f.key === fieldKey)

      if (inputMode === 'edit') {
        return true // Always in edit mode when inputMode is 'edit'
      }

      // For 'read' mode, check if field is editable and currently being edited
      if (inputMode === 'read') {
        return field?.editable !== false && isFieldEditing(fieldKey)
      }

      return false
    },
    [inputMode, fields, isFieldEditing]
  )

  /**
   * Get current value for a field (either editing value or saved value)
   */
  const getFieldValue = useCallback(
    (fieldKey: string): any => {
      if (inputMode === 'edit') {
        return fieldValues[fieldKey]
      }
      return editingFieldData[fieldKey] !== undefined ? editingFieldData[fieldKey] : fieldValues[fieldKey]
    },
    [inputMode, fieldValues, editingFieldData]
  )

  // ============================================================================
  // Focus Management
  // ============================================================================

  // Auto-focus first error field when validation fails
  // Simple approach: Only focus when firstErrorFieldKey is SET, don't change focus during typing
  useEffect(() => {
    if (!firstErrorFieldKey) {
      // Clear ref when firstErrorFieldKey is cleared
      focusedErrorFieldRef.current = null
      return
    }

    // Check if we've already focused this field - prevent re-focusing during typing
    if (focusedErrorFieldRef.current === firstErrorFieldKey) {
      return
    }

    // CRITICAL: Check if user is typing BEFORE doing anything else
    // If user is typing in any input/textarea, don't run focus effect at all
    const activeElement = document.activeElement
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      // User is typing somewhere - don't interrupt, mark as focused to prevent re-runs
      focusedErrorFieldRef.current = firstErrorFieldKey
      return
    }

    const field = fields.find((f) => f.key === firstErrorFieldKey)
    if (!field) {
      focusedErrorFieldRef.current = null
      return
    }

    const isEditing = shouldFieldBeEditing(firstErrorFieldKey)
    if (!isEditing) {
      focusedErrorFieldRef.current = null
      return
    }

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      // Double-check: if user started typing while we were waiting, abort
      const currentActiveElement = document.activeElement
      if (
        currentActiveElement &&
        (currentActiveElement.tagName === 'INPUT' || currentActiveElement.tagName === 'TEXTAREA')
      ) {
        // User started typing - don't interrupt
        focusedErrorFieldRef.current = firstErrorFieldKey
        return
      }

      // Double-check field is still in edit mode and firstErrorFieldKey hasn't changed
      if (!firstErrorFieldKey || !shouldFieldBeEditing(firstErrorFieldKey)) {
        focusedErrorFieldRef.current = null
        return
      }

      // Find the field container
      const fieldWrappers = document.querySelectorAll(`[data-field-key="${firstErrorFieldKey}"]`)
      let targetContainer: Element | null = null
      let fieldWrapper: Element | null = null

      if (fieldWrappers.length > 0) {
        fieldWrapper = fieldWrappers[0]
        targetContainer = fieldWrapper.querySelector('.inline-edit-container')
      }

      // Fallback: if data attribute approach doesn't work, try finding by position
      if (!targetContainer) {
        const fieldContainers = document.querySelectorAll('.inline-edit-container')
        const fieldIndex = fields.findIndex((f) => f.key === firstErrorFieldKey)
        if (fieldIndex !== -1) {
          const containersArray = Array.from(fieldContainers)
          targetContainer = containersArray[fieldIndex] || null
          // Try to find the wrapper element
          if (targetContainer) {
            fieldWrapper = targetContainer.closest('[data-field-key]') || targetContainer.parentElement
          }
        }
      }

      // Scroll field wrapper into view first (smooth scroll)
      if (fieldWrapper && fieldWrapper instanceof HTMLElement) {
        try {
          fieldWrapper.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          })
        } catch (e) {
          // Fallback for browsers that don't support scrollIntoView options
          try {
            fieldWrapper.scrollIntoView()
          } catch (scrollError) {
            // Ignore scroll errors
          }
        }
      }

      if (targetContainer) {
        // Find input elements within the container
        const selectors = [
          'input',
          'textarea',
          '.ant-select-selector',
          '.ant-picker-input input',
          '.ant-time-picker-input',
          '.ant-input',
          '[role="combobox"]',
          '[role="textbox"]',
          '.ant-select'
        ]

        for (const selector of selectors) {
          const input = targetContainer.querySelector(selector) as HTMLElement
          if (input) {
            try {
              if (input.classList.contains('ant-select')) {
                const selectorElement = input.querySelector('.ant-select-selector') as HTMLElement
                if (selectorElement && typeof selectorElement.focus === 'function') {
                  // The wrapper's smooth block:'center' scroll above already
                  // positions the field; the browser's instant focus-scroll
                  // would override it and can park the input under a sticky
                  // FormSection header.
                  selectorElement.focus({ preventScroll: true })
                  focusedErrorFieldRef.current = firstErrorFieldKey
                  setTimeout(() => selectorElement.click(), 50)
                  return
                }
              } else {
                if (typeof input.focus === 'function') {
                  // See the select branch above: keep the smooth scroll.
                  input.focus({ preventScroll: true })
                  focusedErrorFieldRef.current = firstErrorFieldKey
                  return
                }
              }
            } catch (e) {
              // Continue to next selector
            }
          }
        }
      }
    }, 200) // Delay to ensure DOM updates are complete

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstErrorFieldKey, fields, validationErrorsVersion]) // Intentionally exclude shouldFieldBeEditing to prevent re-runs during typing; validationErrorsVersion re-runs it for a repeated explicit submit

  // ============================================================================
  // Render
  // ============================================================================

  // Calculate the maximum label width for proper alignment
  // All labels use this width so values start at the same column position
  const getMaxLabelWidth = () => {
    if (maxLabelWidthProp !== undefined) {
      return typeof maxLabelWidthProp === 'number' ? `${maxLabelWidthProp}px` : maxLabelWidthProp
    }

    const labels = fields
      .filter((field) => field.label)
      .map((field) => {
        const labelText = field.label || ''
        const colon = showLabelColons ? ':' : ''
        // A labelTooltip renders a help "?" after the text. Two characters'
        // worth of width, counted here so the inline layout's shared label
        // column still fits the longest label and the values stay aligned.
        const helpIcon = field.labelTooltip ? '  ' : ''
        return labelText + colon + helpIcon
      })

    if (labels.length === 0) return 'auto'

    // Find the longest label text (including colon)
    const longestLabel = labels.reduce((a, b) => (a.length > b.length ? a : b))

    // Convert to approximate width (roughly 9-10px per character for text-sm bold font)
    // Add buffer for variable character widths and ensure no wrapping
    const estimatedWidth = longestLabel.length * 8

    // Round up and ensure minimum width for very short labels
    const width = Math.ceil(Math.max(estimatedWidth, 120))
    return `${width}px`
  }

  const maxLabelWidth = getMaxLabelWidth()

  // Note: Field values are exposed through individual field onChange callbacks
  // Parent components can track field values through the existing onChange prop

  // Group fields that should be rendered side-by-side (e.g., Date/Time)
  const groupedFields = useMemo(() => {
    const groups: Array<FieldConfig | FieldConfig[]> = []
    let i = 0
    while (i < fields.length) {
      const currentField = fields[i]
      if (currentField.groupWithNext && i + 1 < fields.length) {
        // Group this field with the next one
        groups.push([currentField, fields[i + 1]])
        i += 2
      } else {
        groups.push(currentField)
        i += 1
      }
    }
    return groups
  }, [fields])

  return (
    <div className={className} style={style}>
      {groupedFields.map((fieldOrGroup, groupIndex) => {
        // Handle grouped fields (side-by-side layout)
        if (Array.isArray(fieldOrGroup)) {
          const [field1, field2] = fieldOrGroup
          const isEditing1 = shouldFieldBeEditing(field1.key)
          const isEditing2 = shouldFieldBeEditing(field2.key)
          const currentValue1 = getFieldValue(field1.key)
          const currentValue2 = getFieldValue(field2.key)
          // config.value must be the ORIGINAL (saved) value, not the draft:
          // the field's hasChanges() compares tempValue against it, and with
          // both set to the draft it was constant-false — click-away always
          // took the silent-cancel branch, discarding entered values (the
          // 2026-08-26 owner report).
          const originalValue1 = fieldValues[field1.key]
          const originalValue2 = fieldValues[field2.key]
          const validationError1 = fieldValidationErrors[field1.key]
          const validationError2 = fieldValidationErrors[field2.key]

          // Use per-field labelPosition if specified, otherwise use InputManager's labelPosition
          const field1LabelPosition = field1.labelPosition ?? labelPosition
          const field2LabelPosition = field2.labelPosition ?? labelPosition

          const renderField1 = () => (
            <InputManagerField
              config={{ ...field1, value: isEditing1 ? originalValue1 : currentValue1 }}
              isEditing={isEditing1}
              isSaving={isPatching && isFieldEditing(field1.key)}
              tempValue={currentValue1}
              validationError={validationError1}
              onStartEdit={() => handleStartEdit(field1.key)}
              onConfirm={(overrideValue) => handleConfirm(field1.key, overrideValue)}
              onCancel={() => handleCancel(field1.key)}
              onValueChange={(value) => handleValueChange(field1.key, value)}
              onBlur={() => handleBlur(field1.key)}
              onFieldEdit={onFieldEdit}
              hideActions={inputMode === 'edit'}
              alignment={alignment}
              onClick={onClick}
              record={recordId}
              textClassName={textClassName}
              inputMode={inputMode}
              labelPosition={field1LabelPosition}
            />
          )

          const renderField2 = () => (
            <InputManagerField
              config={{ ...field2, value: isEditing2 ? originalValue2 : currentValue2 }}
              isEditing={isEditing2}
              isSaving={isPatching && isFieldEditing(field2.key)}
              tempValue={currentValue2}
              validationError={validationError2}
              onStartEdit={() => handleStartEdit(field2.key)}
              onConfirm={(overrideValue) => handleConfirm(field2.key, overrideValue)}
              onCancel={() => handleCancel(field2.key)}
              onValueChange={(value) => handleValueChange(field2.key, value)}
              onBlur={() => handleBlur(field2.key)}
              onFieldEdit={onFieldEdit}
              hideActions={inputMode === 'edit'}
              alignment={alignment}
              onClick={onClick}
              record={recordId}
              textClassName={textClassName}
              inputMode={inputMode}
              labelPosition={field2LabelPosition}
            />
          )

          const renderLabel1 = () => {
            if (!field1.label || field1LabelPosition === 'none') return null
            return (
              <label className={`text-black/[0.45] font-normal ${textClassName || 'text-sm'}`}>
                <FieldLabelContent field={field1} showColon={showLabelColons} />
              </label>
            )
          }

          const renderLabel2 = () => {
            if (!field2.label || field2LabelPosition === 'none') return null
            return (
              <label className={`text-black/[0.45] font-normal ${textClassName || 'text-sm'}`}>
                <FieldLabelContent field={field2} showColon={showLabelColons} />
              </label>
            )
          }

          // Render grouped fields side-by-side using Row/Col
          return (
            <div key={`group-${field1.key}-${field2.key}`} className='mb-2'>
              <Row gutter={16}>
                <Col span={12}>
                  <div data-field-key={field1.key} className='scroll-mt-10'>
                    {field1LabelPosition === 'above' ? (
                      <div className='flex flex-col'>
                        {renderLabel1()}
                        <div className='mt-1'>{renderField1()}</div>
                      </div>
                    ) : field1LabelPosition === 'inline' ? (
                      field1.label ? (
                        <div className='flex items-center'>
                          <label
                            className={`text-black/[0.45] font-normal flex-shrink-0 ${textClassName || 'text-sm'}`}
                            style={{ width: maxLabelWidth, paddingRight: '5px' }}
                          >
                            <FieldLabelContent field={field1} showColon={showLabelColons} />
                          </label>
                          <div className='flex-1 min-w-0'>{renderField1()}</div>
                        </div>
                      ) : (
                        renderField1()
                      )
                    ) : (
                      renderField1()
                    )}
                  </div>
                </Col>
                <Col span={12}>
                  <div data-field-key={field2.key} className='scroll-mt-10'>
                    {field2LabelPosition === 'above' ? (
                      <div className='flex flex-col'>
                        {renderLabel2()}
                        <div className='mt-1'>{renderField2()}</div>
                      </div>
                    ) : field2LabelPosition === 'inline' ? (
                      field2.label ? (
                        <div className='flex items-center'>
                          <label
                            className={`text-black/[0.45] font-normal flex-shrink-0 ${textClassName || 'text-sm'}`}
                            style={{ width: maxLabelWidth, paddingRight: '5px' }}
                          >
                            <FieldLabelContent field={field2} showColon={showLabelColons} />
                          </label>
                          <div className='flex-1 min-w-0'>{renderField2()}</div>
                        </div>
                      ) : (
                        renderField2()
                      )
                    ) : (
                      renderField2()
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          )
        }

        // Handle single field (normal rendering)
        const field = fieldOrGroup as FieldConfig
        const isEditing = shouldFieldBeEditing(field.key)
        const currentValue = getFieldValue(field.key)
        // Original (saved) value while editing — see the comment at the
        // grouped-field instantiation above.
        const originalValue = fieldValues[field.key]
        const validationError = fieldValidationErrors[field.key]

        // Use per-field labelPosition if specified, otherwise use InputManager's labelPosition
        const fieldLabelPosition = field.labelPosition ?? labelPosition

        const renderField = () => (
          <InputManagerField
            config={{ ...field, value: isEditing ? originalValue : currentValue }}
            isEditing={isEditing}
            isSaving={isPatching && isFieldEditing(field.key)}
            tempValue={currentValue}
            validationError={validationError}
            onStartEdit={() => handleStartEdit(field.key)}
            onConfirm={(overrideValue) => handleConfirm(field.key, overrideValue)}
            onCancel={() => handleCancel(field.key)}
            onValueChange={(value) => handleValueChange(field.key, value)}
            onBlur={() => handleBlur(field.key)}
            onFieldEdit={onFieldEdit}
            hideActions={inputMode === 'edit'}
            alignment={alignment}
            onClick={onClick}
            record={recordId}
            textClassName={textClassName}
            inputMode={inputMode}
            labelPosition={fieldLabelPosition}
          />
        )

        const renderLabel = () => {
          if (!field.label || fieldLabelPosition === 'none') return null

          return (
            <label className={`text-black/[0.45] font-normal ${textClassName || 'text-sm'}`}>
              <FieldLabelContent field={field} showColon={showLabelColons} />
            </label>
          )
        }

        const isRichTextField = field.type === 'richtext'
        // scroll-mt-10: a scroll-into-view (ours on validation, or the
        // browser's on focus) must land the field BELOW the 32px sticky
        // FormSection header, not under it.
        const fieldWrapperClass = isRichTextField
          ? `mb-2 scroll-mt-10 flex-1 flex flex-col min-h-0 ${field.className || ''}`
          : `mb-2 scroll-mt-10 ${field.className || ''}`

        return (
          <div key={field.key} className={fieldWrapperClass.trim()} data-field-key={field.key}>
            {fieldLabelPosition === 'above' ? (
              <div className={isRichTextField ? 'flex flex-col flex-1 min-h-0' : 'flex flex-col'}>
                {renderLabel()}
                <div className={isRichTextField ? 'mt-1 flex-1 min-h-0' : 'mt-1'}>{renderField()}</div>
              </div>
            ) : fieldLabelPosition === 'inline' ? (
              field.label ? (
                fieldWrapping && isEditing ? (
                  // Vertical layout: label above, field below (when fieldWrapping=true and editing)
                  <div className='flex flex-col space-y-1'>
                    <label className={`text-black/[0.45] font-normal ${textClassName || 'text-sm'}`}>
                      <FieldLabelContent field={field} showColon={showLabelColons} />
                    </label>
                    <div className='flex-1 min-w-0'>{renderField()}</div>
                  </div>
                ) : (
                  // Horizontal layout: label and field side-by-side (default behavior)
                  // All labels use maxLabelWidth so all values start at the same column
                  <div className='flex items-center'>
                    <label
                      className={`text-black/[0.45] font-normal flex-shrink-0 ${textClassName || 'text-sm'}`}
                      style={{ width: maxLabelWidth, paddingRight: '5px' }}
                    >
                      <FieldLabelContent field={field} showColon={showLabelColons} />
                    </label>
                    <div className='flex-1 min-w-0'>{renderField()}</div>
                  </div>
                )
              ) : (
                renderField()
              )
            ) : (
              renderField()
            )}
          </div>
        )
      })}
    </div>
  )
}
